import React from 'react';
import { useNavigate } from 'react-router-dom';
import ContactSalesModal from '../Shared/ContactSalesModal';
import Footer from '../Footer/Footer';
import TexasPricingSection from './TexasPricingSection';
import { MarketService, getAppMarket } from '../../services/MarketService';

/* Texas-specific data — ported from texas.html mockup, no UK-only terms */

const CLASSIFIER_ITEMS = [
  {
    id: 'independent',
    icon: 'fas fa-user',
    iconBg: 'from-amber-400 to-orange-500',
    iconShadow: 'shadow-orange-900/30',
    title: 'Independent / Pro Se Landlord',
    range: '1–50 Units',
    pain: [
      'SB 38 evictions without an attorney',
      '$500+ statutory penalty for missed re-keying',
      "Don't know registration & disclosure rules",
    ],
    solve: [
      'Pro Se Legal Shield — eviction timeline + re-key ledger',
      'Auto-registration reminders',
      'Flood disclosure checklist',
    ],
  },
  {
    id: 'corporate',
    icon: 'fas fa-building',
    iconBg: 'from-teal-400 to-emerald-600',
    iconShadow: 'shadow-emerald-900/30',
    title: 'Corporate Portfolio Investor',
    range: '51–1,000 Units',
    pain: [
      'Fragmented registrations across multiple cities',
      'Tracking 5-year inspections across dozens of SFRs',
      'Entity compliance — franchise tax, registered agent',
    ],
    solve: [
      'Multi-City Dashboard — auto-renew registrations',
      'Self-Certification program tracker',
      'Entity management — Series LLC, franchise tax',
    ],
  },
  {
    id: 'multifamily',
    icon: 'fas fa-city',
    iconBg: 'from-violet-400 to-purple-600',
    iconShadow: 'shadow-purple-900/30',
    title: 'Multifamily Operator',
    range: '100–999 Units',
    pain: [
      'High-risk inspection surge under new ordinances',
      'Fair housing — source-of-income + veteran status',
      'HQS inspections, HAP contracts, Section 8',
    ],
    solve: [
      'On-Site Compliance Buffer — work orders, audit trails',
      'Fair housing module — jurisdiction-specific classes',
      'HAP contract management + HUD inspection prep',
    ],
  },
  {
    id: 'reit',
    icon: 'fas fa-building-skyscraper',
    iconBg: 'from-sky-400 to-indigo-700',
    iconShadow: 'shadow-indigo-900/30',
    title: 'REIT / Institutional Fund',
    range: '1,000+ Units',
    pain: [
      'Acquisitions slowed by messy financial exports',
      'No API integration with Yardi/RealPage/AppFolio',
      'SEC reporting + internal compliance departments',
    ],
    solve: [
      'Acquisitions Accelerator — formatted T12 & rent-roll',
      'API integrations with Yardi, RealPage, AppFolio',
      'Multi-jurisdiction compliance + regulatory tracking',
    ],
  },
];

const PERSONA_VISUALS = [
  { icon: '🔍', num: '1–50', label: 'Units Typical Range' },
  { icon: '🏢', num: '51–1,000', label: 'Units Typical Range' },
  { icon: '🏙️', num: '100–999', label: 'Units Typical Range' },
  { icon: '🏣', num: '1,000+', label: 'Units Typical Range' },
];

const TEXAS_FEATURES = [
  { icon: 'fas fa-gavel', title: 'SB 38 Eviction Shield', desc: '3-day notice generator, 10–21 day hearing tracker, CARES Act override, sworn appeal countdown.' },
  { icon: 'fas fa-key', title: '7-Day Re-Key Ledger', desc: 'Mandatory re-keying tracker tied to tenant turnover. Avoid the $500+ statutory penalty.' },
  { icon: 'fas fa-house-flood-water', title: 'Flood Disclosure', desc: 'Section 92.0135 compliant. FEMA flood zone integration, 5-year history, lease attachment.' },
  { icon: 'fas fa-vault', title: 'Trust Account (IARW)', desc: 'TREC-compliant trust/escrow tracking for licensed PMs. Self-managers see simplified finances.' },
  { icon: 'fas fa-map-pin', title: 'Jurisdiction-Aware Engine', desc: 'Auto-switches requirements across Texas cities — registration, inspection, disclosure rules.' },
  { icon: 'fas fa-scale-balanced', title: 'Fair Housing Compliance', desc: 'Houston: source-of-income + veteran status. Dallas: sexual orientation + gender identity.' },
  { icon: 'fas fa-file-invoice-dollar', title: 'IRS Schedule E + 1099', desc: 'Automated Schedule E mapping, 1099-NEC contractor tracking, one-click rent roll & T12 exports.' },
  { icon: 'fas fa-shield-halved', title: 'HOA & STR Management', desc: 'CC&R compliance checks, Houston STR registration, Dallas STR ban detection, lodging tax tracking.' },
];

const TEXAS_STATS = [
  { num: 'SB 38', label: 'Eviction Reform' },
  { num: '2026', label: 'High-Risk Inspections' },
  { num: '7-Day', label: 'Re-Key Rule' },
  { num: '30-Day', label: 'Deposit Return' },
];

const TexasLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [isContactModalOpen, setIsContactModalOpen] = React.useState(false);
  const [activeClass, setActiveClass] = React.useState(0);
  const appMarket = getAppMarket();
  const marketInfo = MarketService.getMarketInfo(appMarket === 'hub' ? 'texas' : appMarket);

  const trackEvent = (eventName: string, params?: Record<string, string>) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...params });
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
  };

  const currentPersona = CLASSIFIER_ITEMS[activeClass];
  const currentVisual = PERSONA_VISUALS[activeClass];

  const scrollToId = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // F-308: never bounce a Texas visitor to /features (the UK PRS page) —
      // the section is already on this page, so settle at the top instead.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-gray-50 font-sans text-gray-900" data-testid="texas-landing-page">
      {/* NAV — retained UK look and feel, Texas market identity */}
      <nav className="bg-white shadow sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center h-16">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded bg-emerald-600">
              <i className="fas fa-home text-white text-sm"></i>
            </div>
            <span className="font-bold text-lg">Landlord Pro</span>
            <span className="ml-1 bg-red-600 text-white px-2 py-0.5 rounded-full text-xs font-semibold">
              {marketInfo.flagEmoji} Texas
            </span>
          </div>

          <div className="flex items-center gap-6">
            <button
              onClick={() => { trackEvent('cta_click', { button_name: 'features', location: 'nav' }); scrollToId('features'); }}
              className="text-gray-600 hover:text-red-600 font-medium transition-colors"
            >
              Features
            </button>
            <a
              href="https://blog.compliance-engine.io/?s=Landlord"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-red-600 font-medium transition-colors"
            >
              Blog
            </a>
            <button
              onClick={() => { trackEvent('cta_click', { button_name: 'login', location: 'nav' }); navigate('/login'); }}
              className="text-gray-600 hover:text-red-600 font-medium transition-colors"
            >
              Login
            </button>
            <button
              onClick={() => { trackEvent('cta_click', { button_name: 'start_free_trial', location: 'nav' }); navigate('/signup'); }}
              className="bg-red-600 text-white px-5 py-2 rounded font-semibold hover:bg-red-700 transition"
            >
              Start Free Trial
            </button>
          </div>
        </div>
      </nav>

      {/* HERO — retained UK green gradient, Texas headline */}
      <section className="bg-gradient-to-br from-green-600 to-emerald-800 text-white py-4">
        <div className="max-w-screen-2xl mx-auto px-6">
          <div className="text-center mb-4">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 max-w-5xl mx-auto leading-tight" data-testid="texas-hero-title">
              Texas-Engineered
              <br />Compliance
            </h1>
            <p className="text-lg md:text-xl text-green-50 max-w-4xl mx-auto leading-relaxed" data-testid="texas-hero-subtitle">
              SB 38 eviction shield, Dallas Chapter 27 registration, Houston flood disclosure,
              TREC trust-account management — every tool maps to Texas statute.
            </p>
          </div>

          <div className="text-center mb-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#classify"
                onClick={() => trackEvent('cta_click', { button_name: 'find_my_class', location: 'hero' })}
                className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition shadow-lg"
                data-testid="texas-hero-cta"
              >
                <i className="fas fa-compass"></i> Find My Class
              </a>
              <a
                href="#features"
                onClick={(e) => { e.preventDefault(); trackEvent('cta_click', { button_name: 'see_features', location: 'hero' }); scrollToId('features'); }}
                className="inline-flex items-center justify-center gap-2 border border-white/20 bg-white/10 backdrop-blur px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/20 transition"
                data-testid="texas-hero-cta-secondary"
              >
                See Features <i className="fas fa-arrow-right"></i>
              </a>
            </div>
          </div>

          {/* Hero stats — Texas-specific */}
          <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-4" data-testid="texas-hero-stats">
            {TEXAS_STATS.map((stat, i) => (
              <div key={i} className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 flex flex-col text-center">
                <div className="text-3xl font-bold mb-1" data-testid={`texas-hero-stat-num-${i}`}>{stat.num}</div>
                <div className="text-sm text-green-50" data-testid={`texas-hero-stat-label-${i}`}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Persona Classifier — Texas-wide, not city-specific */}
      <section id="classify" className="bg-gray-50" data-testid="texas-classify-section">
        {/* F-309: vertical padding reduced ~70% between this section and
            The Texas Compliance Stack below (was py-4/p-6-level band). */}
        <div className="max-w-7xl mx-auto px-6 pt-3 pb-2">
          <h2 className="text-3xl font-bold text-center mb-2" data-testid="texas-classify-title">
            Which Landlord Class Are You?
          </h2>
          <p className="text-center text-gray-500 mb-2 max-w-2xl mx-auto" data-testid="texas-classify-desc">
            Different class, different compliance burden. Pick yours and see exactly what Landlord Pro does for you.
          </p>

          {/* Persona Tabs */}
          <div className="flex flex-wrap gap-2 mb-2 justify-center" data-testid="class-tabs">
            {CLASSIFIER_ITEMS.map((item, i) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveClass(i)}
                className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all ${
                  activeClass === i
                    ? 'bg-red-600 text-white shadow-lg'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }}`}
                data-testid={`class-tab-${item.id}`}
              >
                {item.title}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-start">
            <div data-testid="class-detail">
              <div
                className="bg-white rounded-2xl shadow p-6 mb-4"
                data-testid={`class-detail-${currentPersona.id}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br ${currentPersona.iconBg} ${currentPersona.iconShadow} shadow-lg`}>
                    <i className={`${currentPersona.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900" data-testid="class-detail-title">{currentPersona.title}</h3>
                    <p className="text-sm text-gray-600" data-testid="class-detail-range">{currentPersona.range}</p>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  {currentPersona.pain.map((p, i) => (
                    <span key={`pain-${i}`} className="inline-flex items-center gap-2 bg-red-50 text-red-700 px-3 py-2 rounded-lg text-sm font-medium">
                      <i className="fas fa-triangle-exclamation"></i> {p}
                    </span>
                  ))}
                </div>

                <div className="space-y-3">
                  {currentPersona.solve.map((s, i) => (
                    <span key={`solve-${i}`} className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-3 py-2 rounded-lg text-sm font-medium">
                      <i className="fas fa-check"></i> {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Persona Visual */}
<div className="bg-white border border-gray-200 rounded-2xl p-8 text-center" data-testid="persona-visual">
                  <div className="text-5xl mb-3" data-testid="persona-icon">{currentVisual.icon}</div>
                  <div className="text-4xl font-extrabold text-red-600 mb-1" data-testid="persona-num">{currentVisual.num}</div>
                  <div className="text-sm text-gray-500 uppercase tracking-wider mb-4" data-testid="persona-label">{currentVisual.label}</div>
                  <button
                    data-testid="persona-start-trial"
                    onClick={() => { trackEvent('cta_click', { button_name: 'start_free_trial', location: 'classifier', persona: currentPersona.id }); if (currentPersona.id === 'independent') { navigate('/signup'); } else { setIsContactModalOpen(true); } }}
                    className="bg-red-600 text-white px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-red-700 transition shadow-sm"
                  >
                    <i className="fas fa-user-plus mr-1"></i> Start Free Trial
                  </button>
                </div>
          </div>
        </div>
      </section>

      {/* Features — Texas-specific, no UK-only terms */}
      <section id="features" data-testid="texas-features-section">
        {/* F-309: top padding tightened to close the empty band above
            The Texas Compliance Stack (part of the -70% request). */}
        <div className="max-w-7xl mx-auto px-6 pt-1 pb-2">
          <h2 className="text-3xl font-bold mb-2">The Texas Compliance Stack</h2>
          <p className="text-gray-500 mb-2 max-w-2xl">
            Every module maps to a Texas statute. No UK artifacts, no deposit protection, no energy-performance certificates.
          </p>

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="texas-feat-grid">
            {TEXAS_FEATURES.map((feat, i) => (
              <div key={i} className="bg-white rounded-2xl shadow p-6 flex flex-col hover:scale-[1.02] transition" data-testid={`texas-feat-card-${i}`}>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${feat.icon} text-red-600 text-xl`}></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-center" data-testid={`texas-feat-title-${i}`}>{feat.title}</h3>
                <p className="text-sm text-gray-500 flex-grow text-center" data-testid={`texas-feat-desc-${i}`}>{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain → Solution */}
      <section className="bg-red-50" data-testid="texas-pain-section">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <h2 className="text-3xl font-bold mb-2">Pain Points → Solutions</h2>
          <p className="text-gray-500 mb-2 max-w-2xl">
            Every Texas landlord faces these. Landlord Pro handles the right one for your class.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="texas-ps-grid">
            {CLASSIFIER_ITEMS[activeClass].pain.map((pain, i) => (
              <div key={`pain-${i}`} className="bg-white border-l-4 border-red-500 rounded-lg p-6 shadow" data-testid={`pain-card-${activeClass}-${i}`}>
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <i className="fas fa-triangle-exclamation text-red-500"></i> {pain}
                </h4>
                <p className="text-sm text-gray-500 mt-1">
                  This is exactly what the {currentPersona.title.split(' / ')[0]} faces under Texas Property Code.
                </p>
              </div>
            ))}
            {CLASSIFIER_ITEMS[activeClass].solve.map((solve, i) => (
              <div key={`solve-${i}`} className="bg-white border-l-4 border-green-500 rounded-lg p-6 shadow" data-testid={`solve-card-${activeClass}-${i}`}>
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <i className="fas fa-check text-green-500"></i> {solve}
                </h4>
                <p className="text-sm text-gray-500 mt-1">
                  Landlord Pro's Texas module handles this automatically.
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white" data-testid="texas-stats-section">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="texas-stats-row">
            {TEXAS_STATS.map((stat, i) => (
              <div key={i} className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center" data-testid={`texas-stat-${i}`}>
                <div className="text-2xl font-extrabold text-red-600 mb-1" data-testid={`texas-stat-num-${i}`}>{stat.num}</div>
                <div className="text-xs text-gray-500 uppercase tracking-wider" data-testid={`texas-stat-label-${i}`}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-green-600 to-emerald-800 text-center py-4" data-testid="texas-cta-section">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-4" data-testid="texas-cta-title">Stop Guessing. Start Complying.</h2>
          <p className="text-green-50 mb-2">
            Identify your landlord class, get the right dashboard, and let Landlord Pro handle the Texas-specific compliance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); trackEvent('cta_click', { button_name: 'start_free_trial', location: 'cta' }); navigate('/signup'); }}
              className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-red-700 transition shadow-lg"
              data-testid="texas-cta-primary"
            >
              {/* F-309: exact CTA wording from the human's screenshot. */}
              <i className="fas fa-user-plus"></i> start 14d free trial no credit card required
            </a>
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); trackEvent('cta_click', { button_name: 'talk_to_sales', location: 'cta' }); setIsContactModalOpen(true); }}
              className="inline-flex items-center justify-center gap-2 border border-white/20 bg-white/10 backdrop-blur px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/20 transition"
              data-testid="texas-cta-secondary"
            >
              Talk to Sales <i className="fas fa-arrow-right"></i>
            </a>
          </div>
        </div>
      </section>

      {/* PRICING (F-285): quiz-driven USD pricing + us2 FastSpring checkout */}
      <TexasPricingSection />

      {/* FOOTER — retained UK look and feel */}
      <Footer />

      {isContactModalOpen && (
        <ContactSalesModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
      )}
    </div>
  );
};

export default TexasLandingPage;
